/**
 * Validation et enregistrement (création ou mise à jour) d'un exercice.
 */

import { useState } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { BookExercise } from '../../../types';
import BookContentApiService from '../../../services/bookContentApi';
import { AppEnvironment } from '../../../config/environment';
import { toUserMessage } from '../../../lib/http';
import { useNotify } from '../../../contexts/NotificationContext';
import { ExerciseFormData } from './types';

/** Vérifie le formulaire ; positionne le message d'erreur et renvoie false si invalide. */
export const validateExerciseForm = (
  formData: ExerciseFormData,
  setError: (error: string) => void
): boolean => {
  if (!formData.title || formData.title.trim().length < 3) {
    setError('Le titre doit contenir au moins 3 caractères');
    return false;
  }
  if (!formData.exerciseNumber || formData.exerciseNumber < 1) {
    setError('Le numéro d\'exercice doit être supérieur ou égal à 1');
    return false;
  }
  if (!formData.points || formData.points < 1 || formData.points > 100) {
    setError('Les points doivent être entre 1 et 100');
    return false;
  }
  if (!formData.lessonId) {
    setError('La leçon parente est requise');
    return false;
  }
  return true;
};

interface UseExerciseSaveParams {
  id: string | undefined;
  env: AppEnvironment;
  isNewMode: boolean;
  formData: ExerciseFormData;
  exercise: BookExercise | null;
  setExercise: (exercise: BookExercise) => void;
  setIsEditMode: (editMode: boolean) => void;
  setError: (error: string) => void;
  navigate: NavigateFunction;
}

/** État « enregistrement en cours » et handler d'enregistrement de l'exercice. */
export const useExerciseSave = ({
  id, env, isNewMode, formData, exercise, setExercise, setIsEditMode, setError, navigate,
}: UseExerciseSaveParams) => {
  const [saving, setSaving] = useState(false);
  const { notify } = useNotify('content');

  const handleSave = async () => {
    setError('');

    if (!validateExerciseForm(formData, setError)) {
      return;
    }

    setSaving(true);
    try {
      if (isNewMode) {
        const response = await BookContentApiService.createBookExercise(env, formData as Omit<BookExercise, 'id'>);
        if (response.data?.id) {
          notify('Exercice créé avec succès', 'success');
          navigate(`/book-exercises/${response.data.id}`, { replace: true });
        } else {
          throw new Error(response.error || 'Erreur lors de la création');
        }
      } else {
        await BookContentApiService.updateBookExercise(env, id!, formData);
        setExercise({ ...exercise, ...formData } as BookExercise);
        setIsEditMode(false);
        notify('Exercice mis à jour avec succès', 'success');
      }
    } catch (err: unknown) {
      setError(toUserMessage(err, 'Erreur lors de la sauvegarde'));
      notify('Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  return { saving, handleSave };
};
